import { Button, ButtonSize, ButtonStyle } from "components/buttons/button/button";
import { FormSelect } from "components/form/form-select/form-select";
import { SearchTextInput } from "components/form/inputs/text-inputs/search-text-input/search-text-input";
import { JSX, useState } from "react";
import { EnumUtils } from "utilities/enumerations/enum-utils";
import { NumberUtils } from "utilities/number-utils";
import { t } from "utilities/localization/t";
import { FormCheckboxInput } from "components/form/form-checkbox-input/form-checkbox-input";
import { ActiveStatus, ActiveStatusStatusDisplayNames } from "models/enumerations/active-status/active-status";
import { InputTypes } from "components/form/enumerations/input-types";
import { TrainingType, TrainingTypeDisplayNames } from "models/enumerations/courses/training-type";
import { useProductSearch } from "utilities/contexts/search/product/product-search-context";
import { Language, LanguageDisplayNames } from "models/enumerations/languages/language";
import { Topic, TopicDisplayNames } from "models/enumerations/courses/topic";

// -------------------------------------------------------------------------------------------------
// #region Interfaces
// -------------------------------------------------------------------------------------------------
interface AdminProductFilterComponentProps {
}
// #endregion Interfaces

// -------------------------------------------------------------------------------------------------
// #region Constants
// -------------------------------------------------------------------------------------------------
const CSS_CLASS_NAME = "admin-product-filter-component";
const DEBOUNCE_TIME = 2000;
// #endregion Constants

// -------------------------------------------------------------------------------------------------
// #region Component
// -------------------------------------------------------------------------------------------------
const AdminProductFilterComponent: React.FC<AdminProductFilterComponentProps> = ({}): JSX.Element => {
    const { initialCriteria: criteria, setCriteria } = useProductSearch();

    const [searchText, setSearchText] = useState<string>(criteria?.searchText ?? "");
    const [productType, setProductType] = useState<TrainingType | undefined>(criteria.productType ?? undefined);
    const [language, setLanguage] = useState<Language | undefined>(criteria?.language ?? undefined);
    const [topic, setTopic] = useState<Topic | undefined>(criteria.topic ?? undefined);
    const [availableForAEN, setAvailableForAEN] = useState<boolean>(criteria?.availableForAEN ?? false);
    const [status, setStatus] = useState<ActiveStatus[]>(criteria?.status ?? [ActiveStatus.Active]);
    
    const handleSearchTriggered = (searchText : string) => {
        setCriteria((prev) => ({ ...prev, 
            searchText: searchText, 
            currentPage: 1 }));
    };

    const handleSearchTextChange = (searchText: string): void => {
        setSearchText(searchText);
    };

    const handleFilterByProductTypeChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        const productTypeIndex = NumberUtils.parseInt(event.target.value) ?? undefined;
        
        const newProductType = (productTypeIndex === undefined) || productTypeIndex < 0 ? undefined : productTypeIndex;
        setProductType(newProductType);
        setCriteria((prev) => ({ ...prev, productType: newProductType, currentPage: 1 }));
    };

    const handleFilterByLanguageChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        const languageIndex = NumberUtils.parseInt(event.target.value) ?? undefined;
        setLanguage(languageIndex);
        setCriteria((prev) => ({ ...prev, language: languageIndex, currentPage: 1 }));
    };

    const handleFilterByTopicChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        const topic = NumberUtils.parseInt(event.target.value) ?? undefined;
        setTopic(topic);
        setCriteria((prev) => {
            return { ...prev, topic: topic, currentPage: 1 };
        });
    };

    const handleSetAvailableForAEN = (value: boolean): void => {
        setAvailableForAEN(value);
        setCriteria((prev) => ({ ...prev, availableForAEN: value, currentPage: 1 }));
    };

    const handleFilterByStatusChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        const activeStatusIndex = NumberUtils.parseInt(event.target.value) ?? 0;
        const newStatus =
            isNaN(activeStatusIndex) || activeStatusIndex < 0
                ? [ActiveStatus.Active]
                : [activeStatusIndex];
        setStatus(newStatus);
        setCriteria((prev) => ({ ...prev, status: newStatus, currentPage: 1 }));
    };

    const handleClearAll = () => {
        setLanguage(undefined);
        setTopic(undefined);
        setAvailableForAEN(false);
        setProductType(undefined);
        setStatus([ActiveStatus.Active]);
        setSearchText("");
        setCriteria((prev) => ({
            ...prev,
            language: undefined,
            topic: undefined,
            availableForAEN: false,
            productType: undefined,
            status: [ActiveStatus.Active],
            searchText: "",
            currentPage: 1,
        }));
    };
    
    return (
        <div className={`${CSS_CLASS_NAME}__sidebar`}>
            <SearchTextInput
                dataTestId={`admin-product-list-page:product-search:input`}
                debounce={DEBOUNCE_TIME}
                onSearchTextInputChange={handleSearchTextChange}
                onSearchTriggered={handleSearchTriggered}
                id={"productSearch"}
                placeholder={t("searchByProductNameOrID")}
                searchTextInputValue={searchText}
            />
            <FormSelect
                ariaLabelledBy={t("filterByType")}
                dataTestId={`admin-product-list-page:filter-by-type:select`}
                formFieldName={t("filterByType")}
                id="filterByType"
                label=""
                placeholder={t("filterByType")}
                onChange={handleFilterByProductTypeChange}
                options={[
                    {text: "", value: ""},
                    ...EnumUtils.numericEnumToSelectOptions(
                        TrainingType,
                        TrainingTypeDisplayNames
                    )
                ]}
                value={productType?.toString()}
            />
            <FormSelect
                ariaLabelledBy={t("filterByLanguage")}
                dataTestId={`admin-product-list-page:filter-by-language:select`}
                formFieldName={t("filterByLanguage")}
                id="instructorFilterByLanguage"
                label=""
                placeholder={t("filterByLanguage")}
                onChange={handleFilterByLanguageChange}
                options={[
                    {text: "", value: ""},
                    ...EnumUtils.numericEnumToSelectOptions(
                    Language,
                    LanguageDisplayNames
                )]}
                value={language?.toString()}
            />
            <FormSelect
                ariaLabelledBy={t("filterByTopic")}
                dataTestId={`admin-product-list-page:filter-by-topic:select`}
                formFieldName={t("filterByTopic")}
                id="instructorFilterByTopic"
                label=""
                placeholder={t("filterByTopic")}
                onChange={handleFilterByTopicChange}
                options={[
                    {text: "", value: ""},
                    ...EnumUtils.numericEnumToSelectOptions(
                    Topic,
                    TopicDisplayNames
                )]}
                value={topic?.toString()}
            />
            <FormSelect
                ariaLabelledBy={t("filterByStatus")}
                dataTestId={`admin-product-list-page:filter-by-status:select`}
                formFieldName={t("filterByStatus")}
                id="productFilterByStatus"
                label=""
                placeholder={t("status")}
                onChange={handleFilterByStatusChange}
                options={EnumUtils.numericEnumToSelectOptions(
                    ActiveStatus,
                    ActiveStatusStatusDisplayNames
                )}
                value={status?.toString()}
            />
            <FormCheckboxInput
                checked={availableForAEN}
                dataTestId={`admin-product-list-page:available-for-aen:checkbox`}
                formFieldName="availableForAEN"
                id="HeaderCheckbox"
                label={t("availableForAen")}
                onChange={handleSetAvailableForAEN}
                type={InputTypes.Checkbox}
                value={availableForAEN}
            />
        
            <Button
                dataTestId={`admin-product-list-page:clear-all-filters:btn`}
                onClick={handleClearAll}
                size={ButtonSize.Medium}
                style={ButtonStyle.Secondary}
                text={t("clearAllFilters")}
            />
        </div>
    );
}

export default AdminProductFilterComponent;